import { useOrganizationScoping } from './useOrganizationScoping';
import { useOrganizationSettingsQuery } from './useOrganizationSettingsQuery';
import { clearThemeCache } from '@/lib/subdomain-utils';

/**
 * Backwards-compatible wrapper around useOrganizationSettingsQuery
 * Provides same API as before, but now uses React Query for shared state
 */
export const useOrganizationSettings = (explicitOrgId?: string) => {
  const { organizationId: scopedOrgId } = useOrganizationScoping();
  
  // Use explicit org ID if provided, otherwise fall back to scoped org ID
  const organizationId = explicitOrgId || scopedOrgId;

  const { 
    settings, 
    isLoading, 
    error, 
    refetch, 
    updateSetting: updateSettingMutation,
    updateSettingAsync,
    isUpdating,
  } = useOrganizationSettingsQuery(organizationId);

  /**
   * Update setting with automatic theme cache clearing
   */
  const updateSetting = async (key: string, value: string, description?: string) => {
    if (!organizationId) return;

    await updateSettingAsync({ key, value, description });

    // Clear theme cache if this is a theme setting
    // React Query will automatically refetch and trigger theme re-application
    if (key.startsWith('theme_')) {
      clearThemeCache(organizationId);
      console.log('[SETTINGS] Cleared theme cache after updating:', key);
    }
  };

  const getSetting = (key: string) => {
    return settings.find(s => s.key === key)?.value || '';
  };

  const getSettingWithDefault = (key: string, defaultValue: string) => {
    return settings.find(s => s.key === key)?.value || defaultValue;
  };

  return {
    settings,
    loading: isLoading,
    updateSetting,
    getSetting,
    getSettingWithDefault,
    refetch,
    isUpdating,
  };
};
